import { Inbox } from 'lucide-react';
import { useEffect, useLayoutEffect, useMemo, useRef } from 'react';

import type {
  CareStateFollowUpStatusT,
  CareStateFollowUpT,
} from '@lotushealth/lotushealth_js_sdk/schemas';

import { useStore } from '@nanostores/react';

import { useCareStateDomain } from '@/entities/care-state-domain';
import { cn } from '@/lib/utils';
import { decodeLotusUuidTimestampMs } from '@/shared/lib';
import {
  $followUpFocusTarget,
  clearFollowUpFocusTarget,
  openChatHistoryAtMessage,
} from '@/shared/stores';
import { EmptyState, TimelineViewer, mapToDaySections } from '@/shared/ui/primitives';

type ChatMessageLink = { conversationId: string; messageId: string };

function toChatMessageLink(link: unknown): ChatMessageLink | null {
  if (link === null || typeof link !== 'object') {
    return null;
  }
  const record = link as Record<string, unknown>;
  if (record.type !== 'chat_message') {
    return null;
  }
  const conversationId = record.conversation_id;
  const messageId = record.message_id;
  if (typeof conversationId !== 'string' || typeof messageId !== 'string') {
    return null;
  }
  return { conversationId, messageId };
}

function FollowUpStatusBadge({ status }: { status: CareStateFollowUpStatusT | undefined }) {
  if (!status) {
    return null;
  }

  const isResponseReceived = status === 'Response Received from Patient';

  return (
    <span
      className={cn(
        'inline-flex items-center rounded-md px-2 py-0.5 text-[10px] font-medium',
        isResponseReceived
          ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
          : 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400'
      )}
    >
      {status}
    </span>
  );
}

function FollowUpItem({
  followUp,
  patientUserId,
}: {
  followUp: CareStateFollowUpT;
  patientUserId: string;
}) {
  const response =
    followUp.response_from_patient === undefined ? null : followUp.response_from_patient;

  const transmission = toChatMessageLink(followUp.transmission_link);
  const acknowledgement = toChatMessageLink(followUp.acknowledgement_link);
  const focusTarget = transmission ?? acknowledgement;
  const isClickable = focusTarget !== null;

  return (
    <button
      type="button"
      data-follow-up-id={followUp.id}
      className={cn('w-full text-left', isClickable ? 'cursor-pointer' : 'cursor-default')}
      onClick={() => {
        if (!focusTarget) {
          return;
        }
        openChatHistoryAtMessage({
          userId: patientUserId,
          conversationId: focusTarget.conversationId,
          messageId: focusTarget.messageId,
        });
      }}
      disabled={!isClickable}
    >
      <div className="text-sm font-medium text-foreground">{followUp.title}</div>

      <div className="mt-1.5 text-xs text-muted-foreground">{followUp.content_to_transmit}</div>

      <div className="mt-2">
        <FollowUpStatusBadge status={followUp.status} />
      </div>

      {response ? (
        <div className="mt-2 rounded-md bg-muted px-2.5 py-1.5 text-xs italic text-foreground">
          {response}
        </div>
      ) : null}
    </button>
  );
}

export function FollowUpsTab() {
  const { careState, patientUserId, update } = useCareStateDomain();
  const hasAutoFocusedRef = useRef(false);
  const followUpFocusTarget = useStore($followUpFocusTarget);

  useEffect(() => {
    if (careState === null) {
      return;
    }

    const unreadFollowUps = careState.follow_ups.filter((f) => f.unread === true);
    if (unreadFollowUps.length === 0) {
      return;
    }

    for (const followUp of unreadFollowUps) {
      update({
        type: 'update',
        table: 'follow_ups',
        row: { ...followUp, unread: false },
        conditionalOldValue: followUp,
      });
    }
  }, [careState, update]);

  useEffect(() => {
    if (careState === null) {
      return;
    }
    if (hasAutoFocusedRef.current) {
      return;
    }

    const firstUnread = careState.follow_ups.find((f) => f.unread === true);
    if (firstUnread === undefined) {
      return;
    }

    hasAutoFocusedRef.current = true;

    const element = document.querySelector(`[data-follow-up-id="${firstUnread.id}"]`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    const chatLink =
      toChatMessageLink(firstUnread.transmission_link) ??
      toChatMessageLink(firstUnread.acknowledgement_link);
    if (chatLink) {
      openChatHistoryAtMessage({
        userId: patientUserId,
        conversationId: chatLink.conversationId,
        messageId: chatLink.messageId,
      });
    }
  }, [careState, patientUserId]);

  useLayoutEffect(() => {
    if (followUpFocusTarget === null) {
      return;
    }
    clearFollowUpFocusTarget();

    const element = document.querySelector(`[data-follow-up-id="${followUpFocusTarget}"]`);
    if (!element) {
      return;
    }
    element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    element.classList.add('bg-accent/60');
    window.setTimeout(() => {
      element.classList.remove('bg-accent/60');
    }, 1500);
  }, [followUpFocusTarget]);

  const sections = useMemo(() => {
    if (careState === null) {
      return [];
    }

    const followUps = careState.follow_ups;
    if (followUps.length === 0) {
      return [];
    }

    return mapToDaySections(followUps, (f) => {
      const ms = decodeLotusUuidTimestampMs(f.id);
      const dayKey = ms !== null ? new Date(ms).toISOString().slice(0, 10) : null;
      return { ...f, __timestampMs: ms, __dayKey: dayKey };
    });
  }, [careState]);

  if (careState === null) {
    return (
      <div className="flex h-full items-center justify-center p-4">
        <EmptyState
          icon={<Inbox className="h-10 w-10" />}
          title="Loading follow-ups"
          description="Fetching follow-ups for this patient."
        />
      </div>
    );
  }

  return (
    <TimelineViewer
      className="min-h-full p-4"
      sections={sections}
      renderItem={(followUp) => <FollowUpItem followUp={followUp} patientUserId={patientUserId} />}
      getItemKey={(followUp) => followUp.id}
      ariaLabel="Follow-ups timeline"
      empty={
        <div className="flex h-full items-center justify-center">
          <EmptyState
            icon={<Inbox className="h-10 w-10" />}
            title="No follow-ups"
            description="Follow-up items for this patient will appear here."
          />
        </div>
      }
    />
  );
}
